 
/* eslint-disable @typescript-eslint/no-unused-vars */
import React, { useState, useMemo, useCallback } from 'react';
import { Package, AlertCircle, Truck, History, CheckCircle, ChevronUp, ChevronDown, Filter } from 'lucide-react';
import { GAME_CONSTANTS } from '../../config/gameConstants';
import type { Product } from '@smartphone-tycoon/shared-core';

// Direct Zustand store imports
import { useInventoryStore } from '../../stores/inventoryStore';
import { useCompanyStore } from '../../stores/companyStore';
import { useGameStore } from '../../stores/gameStore';
import { inventoryManagementService } from '../../services/tycoon/inventoryManagementService';

interface InventoryManagerProps {
  products: Product[];
  cash?: number;
  onCashUpdate?: (newCash: number) => void;
  gameDate?: { day: number; month: number; year: number };
  onEvent?: (event: unknown) => void;
}

// Helper component for sortable table headers
function SortableHeader({ 
  label, 
  sortKey, 
  currentSortKey, 
  direction, 
  onSort 
}: { 
  label: string; 
  sortKey: string; 
  currentSortKey: string; 
  direction: 'asc' | 'desc'; 
  onSort: (key: string) => void;
}) {
  return (
    <th 
      className="px-6 py-3 text-left text-xs font-medium text-theme-text-muted uppercase tracking-wider cursor-pointer hover:bg-theme-bg-tertiary"
      onClick={() => onSort(sortKey)}
    >
      <div className="flex items-center space-x-1">
        <span>{label}</span>
        <div className="flex flex-col">
          <ChevronUp 
            className={`w-3 h-3 ${currentSortKey === sortKey && direction === 'asc' ? 'text-theme-accent' : 'text-theme-text-muted'}`} 
          />
          <ChevronDown 
            className={`w-3 h-3 -mt-1 ${currentSortKey === sortKey && direction === 'desc' ? 'text-theme-accent' : 'text-theme-text-muted'}`} 
          />
        </div>
      </div>
    </th>
  );
}

export function InventoryManager({ products, cash: cashProp, onCashUpdate, gameDate: gameDateProp, onEvent }: InventoryManagerProps) {
  const [activeTab, setActiveTab] = useState<'devices' | 'components' | 'orders'>('devices');
  const [deliveryHistory] = useState<Array<{
    orderId: string;
    componentName: string;
    quantity: number;
    deliveredDate: { day: number; month: number; year: number };
    totalCost: number;
  }>>([]);
  const [showHistory, setShowHistory] = useState(false);
  const [orderError, setOrderError] = useState<string | null>(null);
  const [processingOrder, setProcessingOrder] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  
  // Sorting state
  const [devicesSortColumn, setDevicesSortColumn] = useState<'name' | 'status' | 'stock' | 'value'>('name');
  const [devicesSortDirection, setDevicesSortDirection] = useState<'asc' | 'desc'>('asc');
  const [componentsSortColumn, setComponentsSortColumn] = useState<'name' | 'category' | 'device' | 'stock' | 'value'>('device');
  const [componentsSortDirection, setComponentsSortDirection] = useState<'asc' | 'desc'>('asc');
  
  // Filtering state for components
  const [showFilters, setShowFilters] = useState(false);
  const [componentFilters, setComponentFilters] = useState({
    name: '',
    category: '',
    device: '',
    minStock: '',
    maxStock: ''
  });
  
  // Direct store access
  const storeGameDate = useGameStore(state => state.gameDate);
  const gameDate = gameDateProp || storeGameDate;
  const companyFunds = useCompanyStore(state => state.finances.cash);
  const cash = cashProp ?? companyFunds;
  
  // Inventory store selectors
  const components = useInventoryStore(state => state.components);
  const devices = useInventoryStore(state => state.devices || new Map());
  const purchaseOrders = useInventoryStore(state => state.purchaseOrders);
  const createPurchaseOrder = useInventoryStore(state => state.createPurchaseOrder);
  const spendFunds = useCompanyStore(state => state.spendFunds);
  
  // Calculate metrics directly
  const metrics = useMemo(() => {
    let totalComponentValue = 0;
    let totalDeviceValue = 0;
    const monthlyComponentCost = 0;
    const stockoutRisk = new Map<string, number>();
    
    // Calculate component metrics
    Array.from(components.values()).forEach(comp => {
      const value = comp.quantity * comp.unitCost;
      totalComponentValue += value;
      
      // Estimate monthly usage based on production demand
      // const monthlyUsage = comp.monthlyUsage || 0;
      // monthlyComponentCost += monthlyUsage * comp.unitCost;
      
      // // Calculate stockout risk
      // if (monthlyUsage > 0) {
      //   const daysOfStock = comp.quantity / (monthlyUsage / 30);
      //   if (daysOfStock < 7) {
      //     stockoutRisk.set(comp.id, Math.round((7 - daysOfStock) / 7 * 100));
      //   }
      // }
    });
    
    // Calculate device metrics
    if (devices && devices.size > 0) {
      devices.forEach(device => {
        // Calculate value based on current stock and unit value
        const value = (device.currentStock || 0) * (device.unitValue || 0);
        totalDeviceValue += value;
      });
    }
    
    // Storage cost calculation
    const monthlyStorageCost = Math.round((totalComponentValue + totalDeviceValue) * 0.02);
    
    return {
      totalComponentValue,
      totalDeviceValue,
      monthlyComponentCost,
      monthlyStorageCost,
      stockoutRisk
    };
  // TODO: Fix react-hooks/exhaustive-deps - Missing dependency: devices
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [components]);

  const handleComponentOrder = useCallback(async (componentId: string, quantity: number) => {
    console.log('[InventoryManager] Attempting to place order:', {
      componentId,
      quantity,
      gameDate,
      timestamp: new Date().toISOString()
    });
    
    // Clear any previous errors
    setOrderError(null);
    setProcessingOrder(componentId);
    
    try {
      // Find component details
      const component = components.get(componentId);
      if (!component) {
        setOrderError('Component not found in inventory.');
        return;
      }
      
      const totalCost = quantity * component.unitCost;
      
      // Check funds
      if (totalCost > cash) {
        setOrderError(`Insufficient funds. Need $${totalCost.toLocaleString()}, have $${cash.toLocaleString()}`);
        return;
      }
      
      // Create order through inventory service (this handles database and inventory tracking)
      const serviceOrder = await inventoryManagementService.placeComponentOrder(
        componentId, 
        quantity, 
        gameDate
      );
      
      // Calculate delivery date with proper month/year overflow
      const deliveryDays = gameDate.day + GAME_CONSTANTS.inventory.components.leadTimeDays;
      let deliveryDay = deliveryDays;
      let deliveryMonth = gameDate.month;
      let deliveryYear = gameDate.year;
      
      // Handle month overflow
      while (deliveryDay > 30) {
        deliveryDay -= 30;
        deliveryMonth++;
        if (deliveryMonth > 12) {
          deliveryMonth = 1;
          deliveryYear++;
        }
      }
      
      // DO NOT create a duplicate order in Zustand store
      // The service order created above is the single source of truth
      // Creating duplicate orders causes double delivery issues
      
      // If we need to track orders in Zustand for UI purposes, we should:
      // 1. Load them from the database on component mount
      // 2. Update only the status when orders are delivered
      // 3. Never process Zustand orders for delivery
      
      // For now, we'll skip creating the Zustand order entirely
      // The order will be tracked only in the database
      
      // Add safety check for spendFunds
      if (typeof spendFunds !== 'function') {
        console.error('[InventoryManager] spendFunds is not a function:', {
          type: typeof spendFunds,
          value: spendFunds,
          storeKeys: Object.keys(useCompanyStore.getState())
        });
        setOrderError('Internal error: Financial system unavailable. Please try again.');
        return;
      }
      
      spendFunds(totalCost);
      
      // Legacy callbacks
      if (onCashUpdate) {
        onCashUpdate(cash - totalCost);
      }
      
      if (onEvent) {
        console.log('[InventoryManager] Firing order event via onEvent callback');
        onEvent({
          type: 'component',
          title: 'Component Order Placed',
          description: `Ordered ${quantity.toLocaleString()} units of ${component.componentName} for $${totalCost.toLocaleString()}`,
          impact: 'neutral'
        });
      } else {
        console.log('[InventoryManager] WARNING: onEvent callback is not defined');
      }
      
      // console.log('[InventoryManager] Order placed successfully:', order);
      
    } catch (error) {
      console.error('[InventoryManager] Order error:', error);
      setOrderError(error instanceof Error ? error.message : 'An unexpected error occurred');
    } finally {
      setProcessingOrder(null);
    }
  // TODO: Fix react-hooks/exhaustive-deps - Unnecessary dependency: createPurchaseOrder (stable function from hook)
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [components, cash, gameDate, createPurchaseOrder, spendFunds, onCashUpdate, onEvent]);

  // Clear order error after 5 seconds
  React.useEffect(() => {
    if (orderError) {
      const timer = setTimeout(() => setOrderError(null), 5000);
      return () => clearTimeout(timer);
    }
  }, [orderError]);

  // Load component and device inventory from database on mount
  React.useEffect(() => {
    const loadInventory = async () => {
      try {
        setIsLoading(true);
        // console.log('[InventoryManager] 🚀 Loading inventory from database...');
        await inventoryManagementService.syncComponentInventoryToStore();
        await inventoryManagementService.syncDeviceInventoryToStore();
        // console.log('[InventoryManager] ✅ Component and device inventory loaded from database');
        
        // Debug: Check what's in the store after loading
        // const storeState = useInventoryStore.getState();
        // console.log('[InventoryManager] 📊 Store state after loading:', {
        //   devicesCount: storeState.devices.size,
        //   deviceIds: Array.from(storeState.devices.keys()),
        //   devices: Array.from(storeState.devices.values()).map(d => ({
        //     productId: d.productId,
        //     productName: d.productName,
        //     currentStock: d.currentStock
        //   }))
        // });
      } catch (error) {
        console.error('[InventoryManager] ❌ Failed to load inventory:', error);
      } finally {
        setIsLoading(false);
      }
    };
    
    loadInventory();
  }, []); // Only run once on mount

  const formatNumber = (num: number | undefined): string => {
    if (!num && num !== 0) return '0';
    if (num >= 1000) {
      return `${(num / 1000).toFixed(1)}K`;
    }
    return num.toString();
  };

  // Sorting handlers
  const handleDeviceSort = (column: 'name' | 'status' | 'stock' | 'value') => {
    if (devicesSortColumn === column) {
      setDevicesSortDirection(devicesSortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setDevicesSortColumn(column);
      setDevicesSortDirection('asc');
    }
  };

  const handleComponentSort = (column: 'name' | 'category' | 'device' | 'stock' | 'value') => {
    if (componentsSortColumn === column) {
      setComponentsSortDirection(componentsSortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setComponentsSortColumn(column);
      setComponentsSortDirection('asc');
    }
  };

  // Get production products only - memoized to prevent re-calculations
  const productionProducts = React.useMemo(() => 
    products.filter(p => 
      p.status === 'production' || p.status === 'manufacturing'
    ), [products]);

  const componentProducts = React.useMemo(
    () =>
      products.filter(
        (product) =>
          product.status === 'production' ||
          product.status === 'manufacturing' ||
          product.status === 'released'
      ),
    [products]
  );

  const deviceProducts = React.useMemo(
    () =>
      products.filter(
        (product) =>
          product.status === 'production' ||
          product.status === 'manufacturing' ||
          product.status === 'released'
      ),
    [products]
  );

  // Prepare component data - include ALL components in inventory
  const componentTableData = useMemo(() => {
    const componentData: Array<{
      componentId: string;
      componentName: string;
      category: string;
      deviceName: string;
      deviceId: string;
      inventory: {
        currentStock: number;
        minStock: number;
        unitCost: number;
        componentName: string;
        category: string;
      };
    }> = [];

    // Create a map of components to devices
    const componentToDevices = new Map<string, { deviceName: string; deviceId: string }>();
    componentProducts.forEach((product) => {
      if (product.designConfig?.components) {
        Object.entries(product.designConfig.components).forEach(([, componentId]) => {
          componentToDevices.set(componentId, {
            deviceName: product.name,
            deviceId: product.id
          });
        });
      }
    });

    // Add ALL components from inventory
    Array.from(components.values()).forEach((componentInventory) => {
      const deviceInfo = componentToDevices.get(componentInventory.componentId);
      componentData.push({
        componentId: componentInventory.componentId,
        componentName: componentInventory.componentName,
        category: componentInventory.category || 'unknown',
        deviceName: deviceInfo?.deviceName || 'Not in use',
        deviceId: deviceInfo?.deviceId || '',
        inventory: {
          currentStock: componentInventory.quantity,
          minStock: componentInventory.reorderPoint,
          unitCost: componentInventory.unitCost,
          componentName: componentInventory.componentName,
          category: componentInventory.category
        }
      });
    });

    return componentData;
  }, [componentProducts, components]);

  // Apply sorting and filtering to data
  const sortedDevices = useMemo(() => {
    const sorted = [...deviceProducts].sort((a, b) => {
      let aValue: string | number, bValue: string | number;
      
      switch (devicesSortColumn) {
        case 'name':
          aValue = a.name;
          bValue = b.name;
          break;
        case 'status':
          aValue = a.status;
          bValue = b.status;
          break;
        case 'stock': {
          const devicesArray = devices ? Array.from(devices.values()) : [];
          const aDevice = devicesArray.find(d => d.productId === a.id);
          const bDevice = devicesArray.find(d => d.productId === b.id);
          aValue = aDevice?.currentStock || 0;
          bValue = bDevice?.currentStock || 0;
          break;
        }
        // case 'value': {
        //   const devicesArray = devices ? Array.from(devices.values()) : [];
        //   const aDeviceVal = devicesArray.find(d => d.productId === a.id);
        //   const bDeviceVal = devicesArray.find(d => d.productId === b.id);
        //   const aDeviceValue = (aDeviceVal?.currentStock || 0) * (aDeviceVal?.unitValue || 0);
        //   const bDeviceValue = (bDeviceVal?.currentStock || 0) * (bDeviceVal?.unitValue || 0);
        //   aValue = aDeviceValue;
        //   bValue = bDeviceValue;
        //   break;
        // }
      }
      
      if (typeof aValue === 'string') {
        return devicesSortDirection === 'asc' 
          ? aValue.localeCompare(bValue as string) 
          : (bValue as string).localeCompare(aValue);
      }
      
      return devicesSortDirection === 'asc' ? aValue - bValue : bValue - aValue;
    });
    
    return sorted;
  }, [deviceProducts, devicesSortColumn, devicesSortDirection, devices]);
  const filteredAndSortedComponents = useMemo(() => {
    // First filter
    let filtered = componentTableData;
    
    if (componentFilters.name) {
      filtered = filtered.filter(item => 
        item.componentName.toLowerCase().includes(componentFilters.name.toLowerCase())
      );
    }
    
    if (componentFilters.category) {
      filtered = filtered.filter(item => 
        item.category.toLowerCase().includes(componentFilters.category.toLowerCase())
      );
    }
    
    if (componentFilters.device) {
      filtered = filtered.filter(item => 
        item.deviceName.toLowerCase().includes(componentFilters.device.toLowerCase())
      );
    }
    
    if (componentFilters.minStock) {
      const minStock = parseInt(componentFilters.minStock);
      if (!isNaN(minStock)) {
        filtered = filtered.filter(item => item.inventory.currentStock >= minStock);
      }
    }
    
    if (componentFilters.maxStock) {
      const maxStock = parseInt(componentFilters.maxStock);
      if (!isNaN(maxStock)) {
        filtered = filtered.filter(item => item.inventory.currentStock <= maxStock);
      }
    }
    
    // Then sort
    const sorted = [...filtered].sort((a, b) => {
      let aValue: string | number, bValue: string | number;
      
      switch (componentsSortColumn) {
        case 'name':
          aValue = a.componentName;
          bValue = b.componentName;
          break;
        case 'category':
          aValue = a.category;
          bValue = b.category;
          break;
        case 'device':
          aValue = a.deviceName;
          bValue = b.deviceName;
          break;
        case 'stock':
          aValue = a.inventory.currentStock;
          bValue = b.inventory.currentStock;
          break;
        case 'value':
          aValue = a.inventory.currentStock * a.inventory.unitCost;
          bValue = b.inventory.currentStock * b.inventory.unitCost;
          break;
      }
      
      if (typeof aValue === 'string') {
        return componentsSortDirection === 'asc' 
          ? aValue.localeCompare(bValue) 
          : bValue.localeCompare(aValue);
      }
      
      return componentsSortDirection === 'asc' ? aValue - bValue : bValue - aValue;
    });
    
    return sorted;
  }, [componentTableData, componentFilters, componentsSortColumn, componentsSortDirection]);

  return (
    <div className="space-y-6">
      {/* Order Error Notification */}
      {orderError && (
        <div className="fixed top-20 left-1/2 transform -translate-x-1/2 z-50 max-w-md">
          <div className="bg-theme-error-light border border-theme-error/30 rounded-lg p-4 shadow-lg">
            <div className="flex items-start">
              <AlertCircle className="w-5 h-5 text-theme-error mt-0.5 mr-3 flex-shrink-0" />
              <div className="flex-1">
                <p className="text-sm font-medium text-theme-error-dark">Order Failed</p>
                <p className="text-sm text-theme-error mt-1">{orderError}</p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Inventory Metrics Summary */}
      {metrics && (
        <div className="bg-theme-bg-content rounded-lg shadow p-4">
          <h3 className="text-lg font-semibold mb-3 text-theme-text-primary">Inventory Summary</h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div>
              <p className="text-sm text-theme-text-muted">Total Component Value</p>
              <p className="text-xl font-semibold">${formatNumber(metrics.totalComponentValue)}</p>
            </div>
            <div>
              <p className="text-sm text-theme-text-muted">Total Device Value</p>
              <p className="text-xl font-semibold">${formatNumber(metrics.totalDeviceValue)}</p>
            </div>
            <div>
              <p className="text-sm text-theme-text-muted">Monthly Component Cost</p>
              <p className="text-xl font-semibold">${formatNumber(metrics.monthlyComponentCost)}</p>
            </div>
            <div>
              <p className="text-sm text-theme-text-muted">Storage Cost/Month</p>
              <p className="text-xl font-semibold">${formatNumber(metrics.monthlyStorageCost)}</p>
            </div>
          </div>
        </div>
      )}

      {/* Inventory Tabs */}
      <div className="bg-theme-bg-content rounded-lg shadow">
        <div className="p-4 border-b border-theme-border">
          <h3 className="text-lg font-semibold mb-3 text-theme-text-primary">Inventory Details</h3>
          {/* Tab Navigation */}
          <div className="flex gap-4 border-b">
            <button
              onClick={() => setActiveTab('devices')}
              className={`px-4 py-2 font-medium ${
                activeTab === 'devices'
                  ? 'text-theme-accent border-b-2 border-theme-accent'
                  : 'text-theme-text-muted hover:text-theme-text-secondary'
              }`}
            >
              Devices
            </button>
            <button
              onClick={() => setActiveTab('components')}
              className={`px-4 py-2 font-medium ${
                activeTab === 'components'
                  ? 'text-theme-accent border-b-2 border-theme-accent'
                  : 'text-theme-text-muted hover:text-theme-text-secondary'
              }`}
            >
              Components
            </button>
            <button
              onClick={() => setActiveTab('orders')}
              className={`px-4 py-2 font-medium ${
                activeTab === 'orders'
                  ? 'text-theme-accent border-b-2 border-theme-accent'
                  : 'text-theme-text-muted hover:text-theme-text-secondary'
              }`}
            >
              Orders
            </button>
          </div>
        </div>

        {/* Devices Tab */}
        {activeTab === 'devices' && (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-theme-bg-secondary">
                <tr>
                  <SortableHeader 
                    label="Product" 
                    sortKey="name" 
                    currentSortKey={devicesSortColumn} 
                    direction={devicesSortDirection} 
                    onSort={handleDeviceSort} 
                  />
                  <SortableHeader 
                    label="Status" 
                    sortKey="status" 
                    currentSortKey={devicesSortColumn} 
                    direction={devicesSortDirection} 
                    onSort={handleDeviceSort} 
                  />
                  <SortableHeader 
                    label="Stock" 
                    sortKey="stock" 
                    currentSortKey={devicesSortColumn} 
                    direction={devicesSortDirection} 
                    onSort={handleDeviceSort} 
                  />
                  {/* <SortableHeader 
                    label="Inventory Value" 
                    sortKey="value" 
                    currentSortKey={devicesSortColumn} 
                    direction={devicesSortDirection} 
                    onSort={handleDeviceSort} 
                  /> */}
                </tr>
              </thead>
              <tbody className="bg-theme-bg-content divide-y divide-theme-border">
                {sortedDevices.map((product) => {
                  const devicesArray = devices ? Array.from(devices.values()) : [];
                  const deviceInventory = devicesArray.find(d => d.productId === product.id);
                  const inventoryValue = deviceInventory ? 
                    (deviceInventory.currentStock || 0) * (deviceInventory.unitValue || 0) : 0;
                  return (
                    <tr key={product.id} className="hover:bg-theme-bg-secondary">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <Package className="w-4 h-4 mr-2 text-theme-text-muted" />
                          <div>
                            <div className="font-medium text-theme-text-primary">{product.name}</div>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                          product.status === 'production'
                            ? 'bg-theme-success-light text-theme-success-dark'
                            : product.status === 'manufacturing'
                            ? 'bg-theme-info-light text-theme-info-dark'
                            : product.status === 'released'
                            ? 'bg-theme-accent-light text-theme-accent-dark'
                            : 'bg-theme-bg-tertiary text-theme-text-secondary'
                        }`}>
                          {product.status === 'production' ? 'In Production' :
                           product.status === 'manufacturing' ? 'Manufacturing' :
                           product.status === 'released' ? 'Released' :
                           'Discontinued'}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-theme-text-primary">
                          {deviceInventory ? formatNumber(deviceInventory.currentStock) : '0'}
                        </div>
                      </td>
                      {/* <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-theme-text-primary">
                          {inventoryValue > 0 ? 
                            `$${formatNumber(inventoryValue)}` : 
                            '-'}
                        </div>
                      </td> */}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {/* Components Tab */}
        {activeTab === 'components' && (
          <div>
            {isLoading ? (
              <div className="flex justify-center items-center py-8">
                <div className="text-theme-text-muted">Loading inventory...</div>
              </div>
            ) : (
              <>
                {/* Filter Toggle Button */}
                <div className="px-4 py-2 border-b border-theme-border bg-theme-bg-secondary">
              <button
                onClick={() => setShowFilters(!showFilters)}
                className="flex items-center space-x-2 text-sm text-theme-text-secondary hover:text-theme-text-primary"
              >
                <Filter className="w-4 h-4" />
                <span>{showFilters ? 'Hide Filters' : 'Show Filters'}</span>
              </button>
            </div>
            
            {/* Filter Fields */}
            {showFilters && (
              <div className="px-4 py-3 border-b border-theme-border bg-theme-bg-secondary">
                <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
                  <input
                    type="text"
                    placeholder="Filter by name..."
                    value={componentFilters.name}
                    onChange={(e) => setComponentFilters({ ...componentFilters, name: e.target.value })}
                    className="px-3 py-1 text-sm border border-theme-border rounded-md focus:outline-none focus:ring-1 focus:ring-theme-focus-ring text-theme-text-primary bg-theme-bg-tertiary"
                  />
                  <input
                    type="text"
                    placeholder="Filter by category..."
                    value={componentFilters.category}
                    onChange={(e) => setComponentFilters({ ...componentFilters, category: e.target.value })}
                    className="px-3 py-1 text-sm border border-theme-border rounded-md focus:outline-none focus:ring-1 focus:ring-theme-focus-ring text-theme-text-primary bg-theme-bg-tertiary"
                  />
                  <input
                    type="text"
                    placeholder="Filter by device..."
                    value={componentFilters.device}
                    onChange={(e) => setComponentFilters({ ...componentFilters, device: e.target.value })}
                    className="px-3 py-1 text-sm border border-theme-border rounded-md focus:outline-none focus:ring-1 focus:ring-theme-focus-ring text-theme-text-primary bg-theme-bg-tertiary"
                  />
                  <input
                    type="number"
                    placeholder="Min stock..."
                    value={componentFilters.minStock}
                    onChange={(e) => setComponentFilters({ ...componentFilters, minStock: e.target.value })}
                    className="px-3 py-1 text-sm border border-theme-border rounded-md focus:outline-none focus:ring-1 focus:ring-theme-focus-ring text-theme-text-primary bg-theme-bg-tertiary"
                  />
                  <input
                    type="number"
                    placeholder="Max stock..."
                    value={componentFilters.maxStock}
                    onChange={(e) => setComponentFilters({ ...componentFilters, maxStock: e.target.value })}
                    className="px-3 py-1 text-sm border border-theme-border rounded-md focus:outline-none focus:ring-1 focus:ring-theme-focus-ring text-theme-text-primary bg-theme-bg-tertiary"
                  />
                </div>
                <div className="mt-2 text-sm text-theme-text-secondary">
                  Showing {filteredAndSortedComponents.length} of {componentTableData.length} components
                </div>
              </div>
            )}
            
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-theme-bg-secondary">
                  <tr>
                    <SortableHeader 
                      label="Component" 
                      sortKey="name" 
                      currentSortKey={componentsSortColumn} 
                      direction={componentsSortDirection} 
                      onSort={handleComponentSort} 
                    />
                    <SortableHeader 
                      label="Category" 
                      sortKey="category" 
                      currentSortKey={componentsSortColumn} 
                      direction={componentsSortDirection} 
                      onSort={handleComponentSort} 
                    />
                    <SortableHeader 
                      label="Device" 
                      sortKey="device" 
                      currentSortKey={componentsSortColumn} 
                      direction={componentsSortDirection} 
                      onSort={handleComponentSort} 
                    />
                    <SortableHeader 
                      label="Stock" 
                      sortKey="stock" 
                      currentSortKey={componentsSortColumn} 
                      direction={componentsSortDirection} 
                      onSort={handleComponentSort} 
                    />
                    <SortableHeader 
                      label="Inventory Value" 
                      sortKey="value" 
                      currentSortKey={componentsSortColumn} 
                      direction={componentsSortDirection} 
                      onSort={handleComponentSort} 
                    />
                    <th className="px-6 py-3 text-left text-xs font-medium text-theme-text-muted uppercase tracking-wider">
                      Purchase
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-theme-bg-content divide-y divide-theme-border">
                  {filteredAndSortedComponents.map((item, index) => {
                  const isLowStock = item.inventory.currentStock < item.inventory.minStock;
                  const stockoutRisk = metrics?.stockoutRisk.get(item.componentId) || 0;

                  return (
                    <tr key={`${item.deviceId}-${item.componentId}-${index}`} className="hover:bg-theme-bg-secondary">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="font-medium text-theme-text-primary">
                          {item.componentName}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className="text-sm text-theme-text-secondary">{item.category.toUpperCase()}</span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-theme-text-primary">{item.deviceName}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-theme-text-primary">
                          {formatNumber(item.inventory.currentStock)}
                          {isLowStock && (
                            <AlertCircle className="inline w-4 h-4 ml-1 text-theme-error" />
                          )}
                        </div>
                        {stockoutRisk > 0 && (
                          <div className="text-xs text-theme-error">
                            {stockoutRisk}% stockout risk
                          </div>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-theme-text-secondary">
                          ${formatNumber(item.inventory.currentStock * item.inventory.unitCost)}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex space-x-2">
                          <button
                            onClick={() => handleComponentOrder(item.componentId, 1000)}
                            disabled={cash < item.inventory.unitCost * 1000 || processingOrder === item.componentId}
                            className="px-2 py-1 text-xs font-medium text-theme-accent bg-theme-accent/10 border border-theme-accent rounded hover:bg-theme-accent/20 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
                          >
                            {processingOrder === item.componentId ? '...' : 'Order 1K'}
                          </button>
                          <button
                            onClick={() => handleComponentOrder(item.componentId, 2000)}
                            disabled={cash < item.inventory.unitCost * 2000 || processingOrder === item.componentId}
                            className="px-2 py-1 text-xs font-medium text-theme-accent bg-theme-accent/10 border border-theme-accent rounded hover:bg-theme-accent/20 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
                          >
                            {processingOrder === item.componentId ? '...' : 'Order 2K'}
                          </button>
                          <button
                            onClick={() => handleComponentOrder(item.componentId, 5000)}
                            disabled={cash < item.inventory.unitCost * 5000 || processingOrder === item.componentId}
                            className="px-2 py-1 text-xs font-medium text-theme-accent bg-theme-accent/10 border border-theme-accent rounded hover:bg-theme-accent/20 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
                          >
                            {processingOrder === item.componentId ? '...' : 'Order 5K'}
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
              </>
            )}
          </div>
        )}

        {/* Orders Tab */}
        {activeTab === 'orders' && (
          <div className="p-4">
            {purchaseOrders.filter(o => o.status === 'pending').length > 0 ? (
            <div className="space-y-3">
            {purchaseOrders.filter(o => o.status === 'pending').map(order => {
              // Calculate progress based on game dates
              const orderDate = order.orderDate;
              const deliveryDate = order.expectedDelivery;
              
              if (!orderDate || !deliveryDate || !gameDate || !order.items || order.items.length === 0) {
                return (
                  <div key={order.id} className="border border-theme-border rounded-lg p-3 bg-theme-bg-secondary">
                    <div className="flex justify-between items-center">
                      <div>
                        <span className="font-medium">Component Order</span>
                        <span className="text-sm text-theme-text-muted ml-2">
                          ({order.items ? formatNumber(order.items.reduce((sum, item) => sum + item.quantity, 0)) : '0'} units)
                        </span>
                      </div>
                      <span className="text-sm text-theme-text-secondary">Processing...</span>
                    </div>
                  </div>
                );
              }
              
              // Calculate total days for the order
              const totalDays = GAME_CONSTANTS.inventory.components.leadTimeDays;
              
              // Calculate days elapsed since order
              const daysElapsed = Math.max(0,
                (gameDate.year - orderDate.year) * 360 + 
                (gameDate.month - orderDate.month) * 30 + 
                (gameDate.day - orderDate.day)
              );
              
              // Calculate days remaining
              const daysRemaining = Math.max(0,
                (deliveryDate.year - gameDate.year) * 360 + 
                (deliveryDate.month - gameDate.month) * 30 + 
                (deliveryDate.day - gameDate.day)
              );
              
              // Calculate progress percentage
              const progress = Math.min(100, (daysElapsed / totalDays) * 100);
              
              const isArrivingToday = daysRemaining === 1;
              const isArrivingSoon = daysRemaining === 2;
              
              // For now, handle single item orders (can be extended for multiple items)
              const firstItem = order.items[0];
              const componentName = firstItem.componentName || 'Unknown Component';
              const quantity = order.items.reduce((sum, item) => sum + item.quantity, 0);
              
              return (
                <div key={order.id} className={`border rounded-lg p-3 transition-all duration-300 ${
                  isArrivingToday ? 'bg-theme-success-light border-theme-success/50' :
                  isArrivingSoon ? 'bg-theme-info-light border-theme-info/50' :
                  'bg-theme-bg-secondary'
                }`}>
                  <div className="flex justify-between items-center mb-2">
                    <div className="flex items-center">
                      {isArrivingToday && <Truck className="w-4 h-4 text-theme-success mr-2 animate-pulse" />}
                      <span className="font-medium">{componentName}</span>
                      <span className="text-sm text-theme-text-muted ml-2">
                        ({formatNumber(quantity)} units)
                      </span>
                    </div>
                    <div className="text-sm">
                      <span className={`font-medium ${
                        isArrivingToday ? 'text-theme-success' :
                        isArrivingSoon ? 'text-theme-info' :
                        'text-theme-text-secondary'
                      }`}>
                        {daysRemaining === 0 ? 'Delivered!' : 
                         daysRemaining === 1 ? 'Arriving today!' :
                         daysRemaining === 2 ? 'Arriving tomorrow!' :
                         `${daysRemaining} days remaining`}
                      </span>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="flex-1">
                      <div className="w-full bg-theme-bg-tertiary rounded-full h-2">
                        <div
                          className={`h-2 rounded-full transition-all duration-300 ${
                            isArrivingToday ? 'bg-theme-success' :
                            isArrivingSoon ? 'bg-theme-info' :
                            'bg-theme-text-muted'
                          }`}
                          style={{ 
                            width: `${progress}%` 
                          }}
                        />
                      </div>
                    </div>
                    <span className="text-xs text-theme-text-muted font-medium">
                      {Math.round(progress)}%
                    </span>
                  </div>
                  <div className="flex justify-between items-center mt-1 text-xs text-theme-text-muted">
                    <span>Order value: ${formatNumber(order.totalAmount)}</span>
                    <span>Ordered: {orderDate.month}/{orderDate.day}/{orderDate.year}</span>
                  </div>
                </div>
              );
            })}
            </div>
          ) : (
            <div className="text-center py-8 text-theme-text-muted">
              <Package className="w-12 h-12 mx-auto mb-2 text-theme-text-muted" />
              <p>No pending orders</p>
            </div>
          )}
          </div>
        )}
      </div>

      {/* Order History */}
      {deliveryHistory.length > 0 && (
        <div className="bg-theme-bg-content rounded-lg shadow p-4">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-lg font-semibold flex items-center text-theme-text-primary">
              <History className="w-5 h-5 mr-2" />
              Order History
            </h3>
            <button
              onClick={() => setShowHistory(!showHistory)}
              className="text-sm text-theme-accent hover:text-theme-accent font-medium"
            >
              {showHistory ? 'Hide' : 'Show'} History
            </button>
          </div>
          
          {showHistory && (
            <div className="space-y-2 max-h-60 overflow-y-auto">
              {deliveryHistory.map((delivery, index) => (
                <div 
                  key={`${delivery.orderId}-${index}`} 
                  className="flex items-center justify-between p-3 bg-theme-bg-secondary rounded-lg text-sm"
                >
                  <div className="flex items-center space-x-3">
                    <CheckCircle className="w-4 h-4 text-theme-success" />
                    <div>
                      <span className="font-medium">{delivery.componentName}</span>
                      <span className="text-theme-text-muted ml-2">
                        × {delivery.quantity.toLocaleString()}
                      </span>
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="text-theme-text-secondary">
                      ${delivery.totalCost.toLocaleString()}
                    </div>
                    <div className="text-xs text-theme-text-muted">
                      {delivery.deliveredDate.month}/{delivery.deliveredDate.day}/{delivery.deliveredDate.year}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
